<template>
  <div id="app-area">
    <Header v-if="isLoggedIn && !['Login', '2FASetup', '2FAVerify', 'ChangePassword', null].includes(route.name)" :activeLayout="activeLayout" />

    <div class="app-area-container" :id="bodyAreaId" :class="{ loggedout: !isLoggedIn && route.name !== 'ChangePassword' && !['Login', '2FASetup', '2FAVerify', null].includes(route.name) }">
      <Links v-if="isLoggedIn && route.name !== 'ChangePassword' && !['Login', '2FASetup', '2FAVerify', null].includes(route.name)" @isOpen="(val) => (sideNavisOpen = val)" :activeLayout="activeLayout" />

      <div :sideNavisOpen="!isFullScreen && sideNavisOpen && isLoggedIn && route.name !== 'ChangePassword' && !['Login', '2FASetup', '2FAVerify', null].includes(route.name) ? true : null" :id="contentAreaId">
        <Target />
      </div>
    </div>

    <Footer :activeLayout="activeLayout || ''" />
  </div>

  <DialogWrapper :transition-attrs="{ name: 'dialog' }" />
</template>

<script setup>
import { ref, computed, onMounted, nextTick, onBeforeMount, onBeforeUnmount } from 'vue'
import { store } from './store'
import { useRoute } from 'vue-router'
import { registerUserActivity } from './vuex-actions'
import Target from './components/Target'
import Links from './components/Links'
import Header from './components/Header'
import Footer from './components/Footer'
import { bus } from './main.js'
import { DialogWrapper } from 'vue3-promise-dialog'

const sideNavisOpen = ref(true)
const isFullScreen = ref(false)
const route = useRoute()
const activeLayout = store.getters.activeLayout
const isLoggedIn = store.getters.isLoggedIn

const bodyAreaId = computed(() => {
  if (isLoggedIn && route.name !== 'ChangePassword' && !['Login', '2FASetup', '2FAVerify', null].includes(route.name)) {
    return isFullScreen.value ? 'app-body-area-cp' : 'app-body-area'
  }
  return 'app-body-area-cp'
})

const contentAreaId = computed(() => {
  if ((activeLayout === 'dev' || activeLayout === 'administrative') && !['Login', '2FASetup', '2FAVerify', 'ChangePassword', null].includes(route.name)) {
    return isFullScreen.value ? 'content-area-full' : 'content-area'
  }
  return 'content-area-full'
})

const resTimeout = ref(null)
const handleRes = () => {
  clearTimeout(resTimeout.value)
  resTimeout.value = setTimeout(() => {
    location.reload()
  }, 500)
}

const handleAppAreaResize = () => {
  nextTick(() => {
    const appArea = document.querySelector('.app-area-container')

    if (appArea) {
      const sideMenuContainer = document.getElementById('sidemenu-container')
      const sideMenuWidth = sideMenuContainer ? sideMenuContainer.clientWidth : 350
      const windowWidth = window.innerWidth

      if (sideNavisOpen.value && sideMenuContainer && sideMenuWidth) {
        appArea.style.width = windowWidth - sideMenuWidth + 'px'
      } else {
        appArea.style.width = '100%'
      }
    }
  })
}

// Activity tracking
const activityEvents = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click']
const activityThrottle = ref(null)
const THROTTLE_DELAY = 30000 // 30 seconds

const handleUserActivity = () => {
  // Throttle activity registration to avoid excessive calls
  if (!activityThrottle.value) {
    store.commit(registerUserActivity)

    activityThrottle.value = setTimeout(() => {
      activityThrottle.value = null
    }, THROTTLE_DELAY)
  }
}

const registerActivityListeners = () => {
  activityEvents.forEach((eventType) => {
    document.addEventListener(eventType, handleUserActivity, { passive: true })
  })
}

const removeActivityListeners = () => {
  activityEvents.forEach((eventType) => {
    document.removeEventListener(eventType, handleUserActivity)
  })
}

onBeforeMount(() => {
  localStorage.removeItem('annualQuestionnaireIsOpen')
  localStorage.removeItem('auditBatchData')
})

onMounted(() => {
  bus.on('full-screen-toggle', (val) => {
    isFullScreen.value = val
  })

  setTimeout(() => {
    handleAppAreaResize()
  }, 1500)

  // Register activity listeners only if user is logged in
  if (isLoggedIn) {
    registerActivityListeners()
  }
})

onBeforeUnmount(() => {
  window.removeEventListener('resize', handleRes)
  removeActivityListeners()

  // Clear throttle timeout
  if (activityThrottle.value) {
    clearTimeout(activityThrottle.value)
  }
})
</script>

<style lang="scss">
.admin-dropdown {
  width: 100%;
}

.admin-dropdown .admin-dropdown-trigger {
  width: 100%;
  display: flex;
  gap: 0.55rem;
  padding: 0.52rem;
  color: white !important;
  cursor: pointer;
}

.admin-dropdown input {
  display: none;
}

.admin-dropdown .admin-dropdown-content {
  border-top: 1px solid rgba(201, 201, 201, 0.37);
  border-bottom: 1px solid rgba(201, 201, 201, 0.37);
  width: 100%;
  display: none;
  background-color: #1f314d80;
  margin-left: -0.6rem;
  /* margin-top: ; */
  width: calc(100% + 5.6rem);
  padding-left: 0.3rem;
  padding-block: 0.5rem;
}

.admin-dropdown .admin-dropdown-content .label {
  font-weight: 600;
  color: white;
  margin-left: -0.6rem;
  /* margin-top: ; */
  width: calc(100% + 5.6rem);
  padding-left: 1.5rem;
  padding-block: 0.5rem;
  background-color: rgba(0, 0, 0, 0.356);
}
.admin-dropdown input:checked ~ .admin-dropdown-content {
  display: block;
}

.text-right {
  text-align: right !important;
}

[disabled='disabled'] {
  opacity: 0.25;
  cursor: not-allowed !important;
}

.disabled {
  pointer-events: none;
  cursor: default;
  opacity: 0.5 !important;
  display: flex !important;
}

html {
  font-size: 15px;
}
#app {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 100%;
}

#layout-switcher-area {
  position: absolute;
  top: 0;
  right: 0;
  left: 0;
  height: 42px;
  padding: 5px;
  background-color: #e69138;
}

.modal-background {
  z-index: 40 !important;
}

#app-area {
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  width: 100%;
}

#app-body-area {
  position: absolute;
  top: 132px;
  right: 0;
  bottom: 0;
  padding: 5rem;
}

#app-body-area-cp {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
}

.loggedout {
  top: 0 !important;
}

select {
  z-index: 30;
}

#content-area {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 54px;
  left: 250px;
  /* overflow-y: auto; */
}

@media (max-width: 1000px) {
  .heading {
    width: 100vw;
    transform: translateX(-15%);
  }
}

[sideNavisOpen='false'] {
  width: calc(100vw - 50px) !important;
  transition: all 0.15s ease-in-out !important;
  left: 50px !important;
}

[isOpen='false'] ~ #content-area {
  transition: all 0.15s ease-in-out !important;
  width: calc(100vw - 50px) !important;
  left: 50px !important;
}

.page-wrapper {
  padding: 0rem 2rem !important;
}

.is-fullwidth {
  width: 100% !important;
}

#content-area-full {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 54px;
  left: 0;
  /* overflow-y: auto; */
}

.field.is-grouped .control {
  flex: 1 1 auto;
}

.column > .field {
  flex: 1 1 auto !important;
}

.column > .field > .control {
  width: 100% !important;
}

.column > .field.is-grouped .field,
.column > .field.is-grouped .field > .control {
  width: 100% !important;
}

.text-is-accent {
  color: #7fb942 !important;
}

.button-group {
  display: flex;
  width: 100%;

  justify-content: flex-end;

  margin-bottom: 1rem;
}

.button-group > button {
  margin-left: 1rem;
}

.container-is-fs {
  background-color: #f5f5f5 !important;
  z-index: 10000;
}

/* Firefox */
[scroll],
[scroll] * {
  scrollbar-width: none;
  scrollbar-color: #3c557d #efefef;

  -ms-overflow-style: none; /* IE and Edge */
  scrollbar-width: none; /* Firefox */
}

/* Chrome, Edge and Safari */
[scroll]::-webkit-scrollbar,
[scroll] *::-webkit-scrollbar {
  width: 0 !important;
  display: none;
}

.center-it {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: calc(100vh - 2rem - 52px - 86px - 92px - 1rem) !important;
}

/* Set up  <div id="app" class="target-vue"> & <div data-v*> */
#target-area {
  display: flex;
  flex-direction: column;
  height: 100%;
}

#target-area > div {
  flex-grow: 1;
  position: relative;
  overflow-y: auto;
}
</style>

<style lang="scss">
@import url('https://fonts.googleapis.com/css?family=Poppins');
@import url('https://fonts.googleapis.com/css2?family=Roboto+Mono:ital,wght@0,100;0,200;0,300;0,400;0,500;0,600;0,700;1,100;1,200;1,300;1,400;1,500;1,600;1,700&display=swap');

body,
* {
  font-family: 'Poppins', Helvetica, sans-serif;
}

tbody tr,
.menuItemRow {
  font-size: 14px !important;
}

.label {
  font-weight: normal !important;
}

.is-monospaced,
.is-monospaced * {
  font-family: 'Roboto Mono', monospace !important;
  font-weight: 400;
}

.semi-bold {
  font-weight: 800 !important;
}

.font-italic {
  font-style: italic !important;
}

.magnify {
  animation: magnify 1s infinite !important;
  display: flex;
}

@keyframes magnify {
  0% {
    opacity: 0.5;
  }
  50% {
    opacity: 1;
  }
  100% {
    opacity: 0.5;
  }
}

.fade {
  animation: magnify 1s infinite !important;
  /* opacity: .5!important; */
}

.ellipsis {
  overflow: hidden !important;
  text-overflow: ellipsis !important;
  white-space: nowrap !important;
}

.header--container.is-open ~ #app-body-area[activelayout='osr-facing'] #content-area-full,
.header--container.is-open ~ #app-body-area[activelayout='client-facing'] #content-area-full {
  margin-left: 0 !important;
}

.header--container.is-closed ~ #app-body-area {
  width: 100% !important;
}
</style>
